import { logError } from '@/lib/sentry.lib';

import { isRedirectToCartOnAssetShareEnabled } from '@/feature/feature';
import Button from '@ingka/button';
import { translate } from '@repo/phrase';
import type { AssetDataType } from '@repo/types/asset';
import { isDraft } from '@repo/types/draft';
import { isQuotation } from '@repo/types/quotation';
import { Heading, Paragraph } from '@repo/ui/typography';
import { getStorePrimaryLocale } from '@repo/utils';

import { BarCode } from '@/components/share/barcode';
import { ShareOptions } from '@/components/share/share-options';
import { useShareEventType, useShareForm, useShareMutation } from '@/components/share/share.hooks';

import { useAssetPendingChangesGuard } from '@/hooks/asset-pending-changes-navigation.hooks';
import { useAssetParams } from '@/hooks/asset.hooks';
import { useCustomerDetails } from '@/hooks/customer-details.hooks';
import { navigateToCartHome } from '@/hooks/router.hooks';

import styles from './styles.module.scss';

interface Props {
    onClose: () => void;
    data: AssetDataType;
    initialEmail?: string | undefined;
    initialSms?: string | undefined;
}
export const ShareHeader = ({ onClose, data, initialEmail, initialSms }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const guardPendingChanges = useAssetPendingChangesGuard();
    const { isRegisteredCustomer, isRimsCustomer, preferredLocale } = useCustomerDetails(data);
    const storePrimaryLocale = getStorePrimaryLocale();

    const isDraftOrder = isDraft(data);
    const isQuotationOrder = isQuotation(data);

    const {
        email,
        hasSelectedShareOption,
        locale,
        printer,
        setEmail,
        setLocale,
        setPrinter,
        setSms,
        shouldValidate,
        sms,
        supportedLocales,
        validate,
    } = useShareForm({
        data,
        initialEmail,
        initialSms,
        preferredLocale,
        storePrimaryLocale,
    });

    const eventType = useShareEventType(data);

    const { mutateAsync, isPending } = useShareMutation({
        orderId: assetId,
        assetType,
        data,
        email,
        sms,
        printer,
        locale,
        customerDetails: { isRegisteredCustomer, isRimsCustomer },
        onClose,
        eventType,
    });

    const onSubmit = async () => {
        if (validate()) {
            try {
                await mutateAsync();
                if (isRedirectToCartOnAssetShareEnabled()) {
                    guardPendingChanges(navigateToCartHome);
                }
            } catch (error) {
                logError(error);
            }
        }
    };

    return (
        <div className={styles.container} data-testid="share-header">
            <div className={styles.content}>
                <div className={styles.left_content}>
                    <div className={styles.confirmation_message}>
                        <Heading className={String(styles.heading_text)} headingSize="xl">
                            {translate('share_header_heading_text')}
                        </Heading>
                        <Paragraph className={String(styles.paragraph_text)} bodySize="xl">
                            {translate(
                                isQuotationOrder
                                    ? 'share_header_text_quotation_created'
                                    : 'share_header_text_order_created',
                                { number: assetId }
                            )}
                        </Paragraph>
                        {!isDraftOrder && !isQuotationOrder && <BarCode />}
                    </div>
                </div>
                <div className={styles.share_box}>
                    <div className={styles.sharing}>
                        <Heading>{translate('share_header_text_how_to_share')}</Heading>
                        <ShareOptions
                            data={data}
                            locale={locale}
                            email={email}
                            sms={sms}
                            printer={printer}
                            setEmail={setEmail}
                            setLocale={setLocale}
                            setSms={setSms}
                            setPrinter={setPrinter}
                            shouldValidate={shouldValidate}
                            supportedLocales={supportedLocales}
                        />
                    </div>
                    <div className={styles.action_footer}>
                        <Button
                            text={translate('share_header_button_skip')}
                            onClick={() => {
                                if (isRedirectToCartOnAssetShareEnabled()) {
                                    guardPendingChanges(navigateToCartHome);
                                } else {
                                    onClose();
                                }
                            }}
                        />
                        <Button
                            type="primary"
                            text={translate('share_header_button_send')}
                            loading={isPending}
                            disabled={!hasSelectedShareOption}
                            onClick={() => void onSubmit()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
